import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminLogin } from "./hooks";
import { useAuth } from "../../auth/AuthContext";
import { landingPathFor } from "../../constants/contentSections";
import { useT } from "../../i18n";

const Login: React.FC = () => {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { setAuthenticationState } = useAuth();

  const adminLoginMutation = useAdminLogin();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const result = await adminLoginMutation.mutateAsync({ email, password });
      // Store admin tokens
      localStorage.setItem("admin_access_token", result.data.token);
      localStorage.setItem("admin_refresh_token", result.data.refresh_token);
      localStorage.setItem(
        "adminUserData",
        JSON.stringify(result.data.userData)
      );

      // Update auth context state
      setAuthenticationState(result.data.userData);

      // Admins land on Users; a marketer lands on their first granted section
      navigate(landingPathFor(result.data.userData));
    } catch (err: any) {
      setError(
        t("auth.errors.adminLoginFailed")
      );
    }
  };

  return (
    <div className="bg-white flex items-center justify-center min-h-screen relative font-sans">
      <div className="w-full max-w-md p-10 bg-white rounded-lg border border-gray-300 shadow-md relative z-10">
        <div className="flex flex-col items-center mb-10">
          <div className="relative mb-6">
            <img
              src={process.env.PUBLIC_URL + "/luxia.svg"}
              alt={t("auth.signin.logoAlt")}
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("auth.signin.title")}
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            {t("auth.signin.subtitle")}
          </p>
        </div>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              {t("auth.signin.emailLabel")}
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3.5 pl-12 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-500 transition-all duration-200 placeholder-gray-400"
                placeholder={t("auth.signin.emailPlaceholder")}
                required
              />
              <i className="fa-regular fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="password"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              {t("auth.signin.passwordLabel")}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3.5 pl-12 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-500 transition-all duration-200 placeholder-gray-400"
                placeholder="••••••••"
                required
              />
              <i className="fa-regular fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <button
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                <i
                  className={
                    showPassword
                      ? "fa-regular fa-eye-slash"
                      : "fa-regular fa-eye"
                  }
                ></i>
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="w-4 h-4 text-gray-600 bg-gray-50 border-gray-300 rounded focus:ring-gray-500 focus:ring-2"
              />
              <span className="ml-2 text-gray-600">{t("auth.signin.rememberMe")}</span>
            </label>
            <span className="text-gray-600 hover:text-gray-700 cursor-pointer font-medium">
              {t("auth.signin.forgotPassword")}
            </span>
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <div className="pt-2">
            <button
              type="submit"
              className="w-full bg-gray-700 hover:bg-gray-800 text-white py-3.5 rounded-lg font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-sm disabled:opacity-50"
              disabled={adminLoginMutation.isPending}
            >
              <span className="flex items-center justify-center">
                {adminLoginMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    {t("auth.signin.submitting")}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-arrow-right-to-bracket mr-2"></i>
                    {t("auth.signin.submit")}
                  </>
                )}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
